<template>
  <!-- The figure at the end of a stock-mode row: what a shelf holds, or what a change makes of it. -->
  <span
    v-if="change"
    class="flex shrink-0 items-center gap-1 font-mono text-xs tabular-nums text-surface-500"
  >
    <!-- The row already reads "Saving: …", so the spinner is for the eye alone, and steps aside under reduced
         motion: the label and the hourglass still say it. -->
    <TwnLoadingSpinner
      v-if="isSaving"
      aria-hidden="true"
      class="motion-reduce:hidden"
    />
    {{ change.before }}
    <!-- A screen reader says "right arrow" for the glyph, so it hears "to" instead. -->
    <span aria-hidden="true">→</span>
    <span class="sr-only">to</span>
    <span
      :class="afterClass"
      class="font-semibold"
    >
      {{ change.after }}
    </span>
  </span>
  <span
    v-else-if="shelfLabel"
    :class="shelfClass"
    class="shrink-0 text-xs tabular-nums"
  >
    {{ shelfLabel }}
  </span>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { StockChangeDirection } from '@/domains/command-center/handlers/stock/stockMode.types'

import TwnLoadingSpinner from '@/components/atoms/progress/twn-loading-spinner/TwnLoadingSpinner.vue'
import {
  changeDirection,
  shelfChangeOf,
  shelfVariantOf
} from '@/domains/command-center/handlers/stock/stockShelfChange'
import { useMeasurementUnits } from '@/domains/measurement-units/composables/use-measurement-units/useMeasurementUnits'
import { formatStock } from '@/utils/unit-util/unitUtil'

import type { TwnStockRowTrailingProps } from './TwnStockRowTrailing.types'

const props = defineProps<TwnStockRowTrailingProps>()

/** Green for stock in, red for stock out, plain for a count that lands where it lands. The palette's own tints. */
const DIRECTION_CLASSES: Record<StockChangeDirection, string> = {
  up: 'text-green-700',
  down: 'text-red-700',
  flat: 'text-surface-900'
}

const { stockUnitOf } = useMeasurementUnits()

const shelfChange = computed(() => shelfChangeOf(props.row))

/** Every figure on the row is a base count, read in the variant's stock unit. */
const stockUnit = computed(() => stockUnitOf(shelfVariantOf(props.row)?.pricingUnit))

const stockLabel = (baseCount: number): string => formatStock({ baseCount, unit: stockUnit.value })

const change = computed(() =>
  shelfChange.value
    ? { before: stockLabel(shelfChange.value.before), after: stockLabel(shelfChange.value.after) }
    : null
)

const isSaving = computed(() => props.row.kind === 'saving')

const afterClass = computed(() =>
  shelfChange.value ? DIRECTION_CLASSES[changeDirection(shelfChange.value)] : DIRECTION_CLASSES.flat
)

const isUnstocked = computed(() => props.row.kind === 'variant' && props.row.shelf.item === null)

const shelfLabel = computed(() => {
  if (props.row.kind !== 'variant') return null
  if (isUnstocked.value) return 'Not stocked here'

  return `${stockLabel(props.row.shelf.current)} at ${props.locationName}`
})

// Twine's own yellow, at the shade that passes 4.5:1 for small text on white.
const shelfClass = computed(() => (isUnstocked.value ? 'text-yellow-800' : 'text-surface-500'))
</script>
